import { z } from "zod/v4";

import {
  DescriptionSchema,
  PageElementIdSchema,
  PopupSchema,
} from "../../../functions/opportunity-audit/shopify/page-read";

/** At most this many places on a page take a change in one content test. */
export const MAX_PLACEMENTS = 6;

/** What a sentence can claim, and so what a fact it cites must be able to back. */
export const ClaimKindList = [
  "offer",
  "shipping",
  "price",
  "review",
  "guarantee",
  "product",
  "scarcity",
] as const;

export const ClaimKindSchema = z.enum(ClaimKindList);

/** The read whose element ids a record uses. Ids mean nothing in another read. */
export const ReadRefSchema = z.object({
  url: z.string(),
  source: z.enum(["html", "rendered", "template"]),
  version: z.number(),
});

/** One element of the read a change is built at, as a reader would find it. */
export const AnchorSchema = z.object({
  element: PageElementIdSchema,
  description: DescriptionSchema,
  kind: z.enum(["text", "media", "control", "group"]),
  /** What a shopper reads there; quoted as Today. */
  text: z.string(),
  /** The product it speaks of, only with evidence. */
  about: z.string().nullable(),
  /** How it is built, so a brief can say what style a new element takes. Records before step 3 have none. */
  markup: z
    .object({
      tag: z.string(),
      /** Its first three classes. */
      classes: z.array(z.string()),
      set: z.object({ size: z.number(), index: z.number() }).nullable(),
      /** It sits in the buy box of its product's page. */
      buyBox: z.boolean(),
    })
    .optional(),
});

export const FactSourceSchema = z.object({
  type: z.enum(["page", "popup", "storeSetting", "catalog", "productPage"]),
  /** Written by code, like "an email sign-up popup". */
  label: z.string(),
  /** A store path with its query, or the element's own link. */
  url: z.string().nullable(),
  /** Drawn by a script after the page loaded. */
  afterLoad: z.boolean(),
});

/** Something the store states, which a line may rest on. */
export const FactSchema = z.object({
  /** `e12` for an element of the read, `O3` for an observation, `F1` for a store read. */
  id: z.string(),
  /** The claims it can back. */
  kinds: z.array(ClaimKindSchema),
  /** As the store states it. */
  text: z.string(),
  about: z.string().nullable(),
  source: FactSourceSchema,
  popup: PopupSchema.shape.kind.nullable(),
});

/** One line of new shopper copy, exactly as it will show. */
export const LineSchema = z.object({
  text: z.string(),
  cites: z.array(z.string()).min(1),
  /** Written on cards before 2026-10-19, by code; no longer written. */
  claims: z.array(ClaimKindSchema).optional(),
  /** Spans in quotation marks, found by code. */
  quotes: z.array(z.string()),
  headline: z.boolean(),
  /** The url of the cited fact the line links to. */
  href: z.string().nullable(),
});

export const PlacementSchema = z.object({
  anchor: AnchorSchema,
  action: z.enum([
    "replace",
    "insertBefore",
    "insertAfter",
    "rework",
    "moveBefore",
    "moveAfter",
    "hide",
    "replaceImage",
  ]),
  /** Where a move puts the anchor: the element it goes next to. */
  target: AnchorSchema.nullable().optional(),
  /**
   * Copy written word for word for a replace or insert, in records from before
   * Viv wrote the copy. Empty for every other action, in a `missing` record,
   * and where `treatment` describes the copy.
   */
  lines: z.array(LineSchema),
  /** The change in words, for Viv to build: a rework, or the copy a replace or insert adds. */
  treatment: z
    .object({ text: z.string(), cites: z.array(z.string()).min(1) })
    .nullable(),
});

/** A fact copy would need and the store did not give. All words written by code. */
export const MissingFactSchema = z.object({
  /** Any one of them would do. */
  kinds: z.array(ClaimKindSchema),
  /** Like "an offer the store runs". */
  needed: z.string(),
  /** Like "automatic discounts". */
  looked: z.array(z.string()),
});

export const ContentUnreadReasonSchema = z.enum([
  "pageUnread",
  "pageMoved",
  "placeGone",
  "cart",
  "nothingToAnchor",
  "cannotName",
  "incomplete",
]);

const placements = z.array(PlacementSchema).min(1).max(MAX_PLACEMENTS);

/** What a described change does to its place. */
export const ContentChangeKindList = [
  /** Something new in the place. */
  "add",
  /** The place's own words. */
  "reword",
  /** The place as a whole. */
  "rework",
  /** Where something in the place sits on the page. */
  "move",
  /** Something in the place, out of view. */
  "hide",
  /** The place's pictures, for ones the merchant provides. */
  "replaceImages",
] as const;
export const ContentChangeKindSchema = z.enum(ContentChangeKindList);

/** The place a described change is made in, as a builder finds it. */
export const ChangePlaceSchema = z.object({
  /** Like "the newsletter section" or "the page's own content". */
  name: z.string(),
  /** The section's selector; null for the page as a whole. */
  selector: z.string().nullable(),
});

/**
 * What to build for one content pick. `described` names the place and the
 * change in words, and the builder, who sees the page, decides which elements
 * it touches and what stays. `exact` and `missing` name elements of one read;
 * they are no longer written, and stored cards keep them. `unread` says why
 * the page could not be named.
 */
export const AuditContentSpecSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("described"),
    version: z.literal(2),
    read: ReadRefSchema,
    place: ChangePlaceSchema,
    /** What the place shows a shopper at load; the card's Today. */
    today: z.string(),
    kind: ContentChangeKindSchema,
    /** The change in one sentence, starting in lower case; the card's Proposed. */
    change: z.string(),
    /** The facts and elements it rests on. */
    cites: z.array(z.string()),
    /** Every fact something on the card cites. */
    facts: z.array(FactSchema),
  }),
  z.object({
    type: z.literal("exact"),
    version: z.literal(1),
    read: ReadRefSchema,
    placements,
    /** What stays as it is around the change. */
    keep: z.array(AnchorSchema),
    /** Every fact something on the card cites. */
    facts: z.array(FactSchema),
  }),
  z.object({
    type: z.literal("missing"),
    version: z.literal(1),
    read: ReadRefSchema,
    /** Where copy would go, with no lines. */
    placements,
    missing: z.array(MissingFactSchema).min(1),
    facts: z.array(FactSchema),
  }),
  z.object({
    type: z.literal("unread"),
    version: z.literal(1),
    reason: ContentUnreadReasonSchema,
  }),
]);

export type ClaimKindType = z.infer<typeof ClaimKindSchema>;
export type ReadRefType = z.infer<typeof ReadRefSchema>;
export type AnchorType = z.infer<typeof AnchorSchema>;
export type FactType = z.infer<typeof FactSchema>;
export type LineType = z.infer<typeof LineSchema>;
export type PlacementType = z.infer<typeof PlacementSchema>;
export type MissingFactType = z.infer<typeof MissingFactSchema>;
export type ContentUnreadReasonType = z.infer<typeof ContentUnreadReasonSchema>;
export type AuditContentSpecType = z.infer<typeof AuditContentSpecSchema>;
export type ContentChangeKindType = z.infer<typeof ContentChangeKindSchema>;
export type ChangePlaceType = z.infer<typeof ChangePlaceSchema>;
